import { EControlType } from "components/ControlWrapper";
import { friendlyValue } from "global/Application";
import { IGCDataLight } from "global/Reducer/dataSlice";
import { ArrayToLookupByKey, groupByWithAggregate } from "global/Utility/TSFunctions";
import { bcbs_config_attribute } from "model/CRM";
import {
    IBenefit as FAM_IBenefit,
    ICESNumber as FAM_ICESNumber,
    IDepartment as FAM_IDepartment,
    IFlexibleField as FAM_IFlexibleField,
    IFlexibleFieldSet as FAM_IFlexibleFieldSet,
    IFlexibleFieldValue as FAM_IFlexibleFieldValue,
    IGroupConfigurationData as FAM_IGroupConfigurationData,
    IGroupNumber as FAM_IGroupNumber,
    INonStandard as FAM_INonStandard,
    INonStandardTemplate as FAM_INonStandardTemplate,
    IPlan as FAM_IPlan,
    ITierLevel as FAM_ITierLevel
} from "model/FlexibleAttributeModel";
import { ICompareConfiguration } from "views/BlueRelationship/CompareLookupDialog/CompareLookupDialog";
import { aggPredicate, ExtractFees } from "views/BlueRelationship/MonthlyRecurringFeesAgg";

type IDepartmentEntity = IDataEntityLike & FAM_IDepartment;

export enum ECompareStatus {
    Unknown = 'Unknown',

    // new on target, not on origin
    OnTargetOnly = 'OnTargetOnly',

    // not on target, only on origin
    OnOriginOnly = 'OnOriginOnly',

    // match on origin and target, children match as well, has not been changed
    Identical = 'Identical',

    // match on origin and target, but has been changed, or children changed
    Modified = "Modified",
}

enum ESide {
    Origin = 1,
    Target = 2,
}

export enum EModelContext {
    unknown = 'unknown',
    plans = 'plans',
    benefits = 'benefits',
    accountsReceivable = 'accountsReceivable',
    groupConfiguration = 'groupConfiguration',
    cesNumbers = 'cesNumbers',
    nonStandardTemplates = 'nonStandardTemplates',
    feesAndPremiums = 'feesAndPremiums',
    stopLoss = 'stopLoss',
    departments = 'departments',
    groupNumbers = 'groupNumbers',
    nonStandards = 'nonStandards',
    attributeSets = 'attributeSets',
    feesSummary = 'feesSummary',
    stopLossSummary = 'stopLossSummary',
}


export const modelContextToFriendlyName: Record<EModelContext, string> = {
    [EModelContext.unknown]: 'Unknown',
    [EModelContext.groupConfiguration]: 'Group Configuration',
    [EModelContext.nonStandardTemplates]: 'Non-Standard Templates',
    [EModelContext.nonStandards]: 'Non-Standards',
    [EModelContext.plans]: 'Plans',
    [EModelContext.benefits]: 'Benefits',
    [EModelContext.accountsReceivable]: 'Accounts Receivable',
    [EModelContext.feesAndPremiums]: 'Fees and Premiums',
    [EModelContext.stopLoss]: 'Stop Loss',
    [EModelContext.groupNumbers]: 'Group Numbers',
    [EModelContext.cesNumbers]: 'CES Numbers',
    [EModelContext.departments]: 'Departments',
    [EModelContext.attributeSets]: 'Attribute Sets',
    [EModelContext.feesSummary]: 'Fee Summary',
    [EModelContext.stopLossSummary]: 'Stop Loss Summary',
}


export interface ICompareContext {
    name: EModelContext;
}

export interface IComparable<TSourceType, TSourceTypeMerged> {
    primaryKey: string;
    onOrigin: TSourceType | undefined,
    onTarget: TSourceType | undefined,
    merged: TSourceTypeMerged | undefined,
    compareStatus: ECompareStatus,
    context?: ICompareContext[];
};

export interface IComparisonEngineInstructions {
    compareRoot?: boolean;
    compareStopLoss?: boolean;
    compareFees?: boolean;
    compareClaimsARs?: boolean;
    compareGroupNumbers?: boolean;
    compareCESNumbers?: boolean;
    compareDepartments?: boolean;
    compareNonStandardTemplates?: boolean;
    compareBenefits?: boolean;
    comparePlans?: boolean;
    useDateFormatter?: true;
    compareConfiguration?: ICompareConfiguration;
}

export interface IFlexibleFieldValueMerged {
    order: number;
    tier: FAM_ITierLevel | null;
    field: FAM_IFlexibleField | null;
    value: FAM_IFlexibleFieldValue | null;
    friendlyValue: string | undefined;
}

export type TComparableResultString = Omit<IComparable<string, string>, 'onMerge'>;

export interface IFlexibleFieldValuesCompared extends Record<string, IComparable<IFlexibleFieldValueMerged, undefined>> {

}

export interface IDataFlexibleFieldLikeMerged {
    configRef: bcbs_config_attribute | null;
    mergedValues: IFlexibleFieldValuesCompared;
}

export interface IDataEntityLikeMerged {
    key: string,

    name?: string,


    mergedProperties: Record<string, TComparableResultString>,

    mergedTiers: Record<string, IComparable<IDataEntityTierLike, IDataEntityTierLike>> | undefined,

    mergedReferences: Record<string, TComparableResultString> | undefined,

    mergedFields: Record<string, IComparable<FAM_IFlexibleField, IDataFlexibleFieldLikeMerged>> | undefined,

    mergedAttributeSets: Record<string, IComparable<FAM_IFlexibleFieldSet, IDataEntityLikeMerged>> | undefined,
    mergedAttributeSetsByRefId?: Record<string, IComparable<FAM_IFlexibleFieldSet, IDataEntityLikeMerged>[]> | undefined,

    mergedNonStandards: Record<string, IComparable<FAM_INonStandard, IDataEntityLikeMerged>> | undefined,
    mergedNonStandardsBySectionRef?: Record<string, IComparable<FAM_INonStandard, IDataEntityLikeMerged>[]> | undefined;
}

interface IDataEntityTierLike {
    id: string;
    name: string;
    bcbs_name?: string;
}

export interface IDataEntityLike {
    id: string,
    name?: string,
    tiers?: FAM_ITierLevel[],
    fields: FAM_IFlexibleField[],
    attributeSets?: FAM_IFlexibleFieldSet[],
    nonStandards?: FAM_INonStandard[],
    // sectionRef
    // referenceOnly
    deactivated?: boolean
};

type ICESNumberEntity = IDataEntityLike & FAM_ICESNumber;


const COMPARE_INSTRUCTIONS_DEFAULT: IComparisonEngineInstructions = {
    compareBenefits: true,
    compareFees: true,
    compareClaimsARs: true,
    compareGroupNumbers: true,
    compareCESNumbers: true,
    compareDepartments: true,
    compareNonStandardTemplates: true,
    comparePlans: true,
    compareStopLoss: true,
    useDateFormatter: true,
    compareRoot: true,
};

export default class ComparisonEngine {

    instructions: IComparisonEngineInstructions;

    originState: IGCDataLight;
    targetState: IGCDataLight;

    groupConfiguration: Record<string, IComparable<IDataEntityLike, IDataEntityLikeMerged>> = {};

    plans: Record<string, IComparable<IDataEntityLike, IDataEntityLikeMerged>> = {};
    benefits: Record<string, IComparable<IDataEntityLike, IDataEntityLikeMerged>> = {};
    groupNumbers: Record<string, IComparable<IDataEntityLike, IDataEntityLikeMerged>> = {};
    cesNumbers: Record<string, IComparable<IDataEntityLike, IDataEntityLikeMerged>> = {};
    departments: Record<string, IComparable<IDataEntityLike, IDataEntityLikeMerged>> = {};
    nonStandardTemplates: Record<string, IComparable<IDataEntityLike, IDataEntityLikeMerged>> = {};
    stopLosses: Record<string, IComparable<IDataEntityLike, IDataEntityLikeMerged>> = {};;
    feesAndPremiums: Record<string, IComparable<IDataEntityLike, IDataEntityLikeMerged>> = {};
    feesSummary: Record<string, IComparable<IDataEntityLike, IDataEntityLikeMerged>> = {};
    stopLossSummary: Record<string, IComparable<IDataEntityLike, IDataEntityLikeMerged>> = {};
    accountsReceivables: Record<string, IComparable<IDataEntityLike, IDataEntityLikeMerged>> = {};


    private getGroupConfigurationProperties = (entity: FAM_IGroupConfigurationData): Record<string, string | undefined> => {
        return {
            name: entity.name,
            deactivated: entity.deactivated !== undefined
                ? (entity.deactivated ? "Yes" : "No")
                : undefined,
            bcbs_shortname: entity.bcbs_shortname,
            effectiveDate: entity.effectiveDate !== undefined
                ? entity.effectiveDate?.value
                : undefined,
            lineOfBusiness: entity.lineOfBusiness !== undefined
                ? entity.lineOfBusiness?.name
                : undefined,
            changeType: entity.changeType !== undefined
                ? entity.changeType?.name
                : undefined,
            dualFunded: entity.dualFunded !== undefined
                ? (entity.dualFunded?.value ? "Yes" : "No")
                : undefined,
            erisa: entity.erisa !== undefined
                ? (entity.erisa?.value ? "Yes" : "No")
                : undefined,
            fundingType: entity.fundingType !== undefined
                ? entity.fundingType?.name :
                undefined,
            grandFathered: entity.grandFathered !== undefined
                ? entity.grandFathered?.name
                : undefined,
            MNIL: entity.MNIL !== undefined
                ? (entity.MNIL?.value ? "Yes" : "No")
                : undefined,
            reconciliation: entity.reconciliation !== undefined
                ? (entity.reconciliation?.value ? "Yes" : "No")
                : undefined,
        }
    }


    private getPlanProperties = (entity: FAM_IPlan): Record<string, string | undefined> => {
        const record: Record<string, string | undefined> = {
            name: entity.name ?? undefined,
            deactivated: entity.deactivated ? "Yes" : "No",
            fundingType: entity.fundingType?.name ?? undefined,
            grandFathered: entity.grandFathered?.name ?? undefined,
            billingOnlyPlan: entity.billingOnlyPlan ? "Yes" : "No",
            feesAndPremiumsRef: entity.feesAndPremiumsRef?.name ?? undefined,
        }
        return record;
    }

    private getBenefitProperties = (entity: FAM_IBenefit): Record<string, string | undefined> => {
        return {
            name: entity.name ?? undefined,
            deactivated: entity.deactivated ? "Yes" : "No",
            fundingType: entity.fundingType?.name ?? undefined,
            grandFathered: entity.grandFathered?.name ?? undefined,
            product: entity.product?.name ?? undefined,
            status: entity.status?.name ?? undefined,
            tierSetRef: entity.tierSetRef?.name ?? undefined,
        }
    }

    private getGroupNumberProperties = (entity: FAM_IGroupNumber): Record<string, string | undefined> => {
        return {
            name: entity.groupNumber ?? undefined,
            deactivated: entity.deactivated ? "Yes" : "No",
            prefix: entity.prefix ?? undefined,
            base: entity.base ?? undefined,
            suffix: entity.suffix ?? undefined,
            groupNumber: entity.groupNumber ?? undefined,
            cesNumberRef: entity.cesNumberRef?.name ?? undefined,
            planRef: entity.planRef?.name ?? undefined,
            stopLossRef: entity.stopLossRef?.name ?? undefined,
            accountsReceivableRef: entity.accountsReceivableRef?.name ?? undefined,
            status: entity.status?.name ?? undefined,
        }
    }

    private getDepartmentProperties = (entity: IDepartmentEntity): Record<string, string | undefined> => ({
        name: entity.name ?? "",
        description: entity.description ?? "",
        cesNumberRef: entity.cesNumberRef?.name ?? "",
        deactivated: entity.deactivated ? "Yes" : "No",
    });

    private getNonStandardTemplateProperties = (entity: FAM_INonStandardTemplate): Record<string, string | undefined> => ({
        name: entity.name ?? "",
        deactivated: entity.deactivated ? "Yes" : "No",
        referenceOnly: entity.referenceOnly ? "Yes" : "No",
        linkedCategoryName: entity.linkedCategoryName ?? "",
    });



    private getCESNumberProperties = (entity: ICESNumberEntity): Record<string, string | undefined> => ({
        name: entity.cesNumber ?? "",
        deactivated: entity.deactivated ? "Yes" : "No",
    });

    private getNonStandardProperties = (entity: FAM_INonStandard): Record<string, string | undefined> => {

        const ret: Record<keyof Omit<FAM_INonStandard, 'id' | 'sectionRef' | 'fields'>, string | undefined> = {
            name: entity.name ?? "",
            deactivated: entity.deactivated ? "Yes" : "No",
            referenceOnly: entity.referenceOnly ? "Yes" : "No",
            partOfTemplate: entity.partOfTemplate ? "Yes" : "No",
        }

        return ret;
    }

    constructor(originState: IGCDataLight,
        targetState: IGCDataLight,
        instructions?: IComparisonEngineInstructions
    ) {
        this.originState = originState;
        this.targetState = targetState;


        // TODO use DeepMerge
        this.instructions = { ...COMPARE_INSTRUCTIONS_DEFAULT, ...instructions };
    }

    // this maybe a long running process
    // we need to run it in iterations of the event loop to prevent blocking the UI
    // TODO error handling and failure recovery
    async processAsync() {

        // Group Configuration
        // -------------------------------
        // run in the next iteration of the event loop, a chance to run context tasks
        if (this.instructions.compareRoot)
            this.groupConfiguration = await new Promise((resolve) => {
                setImmediate(() => {
                    const mergedEntities = this.processEntity(
                        [this.originState.json],
                        [this.targetState.json],
                        [{ name: EModelContext.groupConfiguration }],
                        this.getGroupConfigurationProperties,
                        (entity) => "root"
                    );

                    resolve(mergedEntities);
                });
            });

        // Plans
        // -------------------------------
        // run in the next iteration of the event loop, a chance to run context tasks
        if (this.instructions.comparePlans)
            this.plans = await new Promise((resolve) => {
                setImmediate(() => {
                    const mergedEntities = this.processEntity(
                        this.originState.json.plans,
                        this.targetState.json.plans,
                        [{ name: EModelContext.plans }],
                        this.getPlanProperties,
                        null,
                    );

                    this.processEntityPlan(mergedEntities);

                    resolve(mergedEntities);
                });
            });

        // Benefits
        // -------------------------------
        // run in the next iteration of the event loop, a chance to run context tasks
        if (this.instructions.compareBenefits)
            this.benefits = await new Promise((resolve) => {
                setImmediate(() => {
                    const mergedEntities = this.processEntity(
                        this.originState.json.benefits,
                        this.targetState.json.benefits,
                        [{ name: EModelContext.benefits }],
                        this.getBenefitProperties,
                        null,
                    );

                    this.processEntityBenefit(
                        this.originState,
                        this.targetState,
                        this.originState.json.plans,
                        this.targetState.json.plans,
                        mergedEntities,
                    )

                    resolve(mergedEntities);
                });
            });

        // Group Numbers
        // -------------------------------
        // run in the next iteration of the event loop, a chance to run context tasks
        if (this.instructions.compareGroupNumbers)
            this.groupNumbers = await new Promise((resolve) => {
                setImmediate(() => {
                    const mergedEntities = this.processEntity(
                        this.originState.json.groupStructure?.groupNumbers,
                        this.targetState.json.groupStructure?.groupNumbers,
                        [{ name: EModelContext.groupNumbers }],
                        this.getGroupNumberProperties,
                        null,
                    );
                    resolve(mergedEntities);
                });
            });


        // CES Numbers
        // -------------------------------
        // run in the next iteration of the event loop, a chance to run context tasks
        if (this.instructions.compareCESNumbers)
            this.cesNumbers = await new Promise((resolve) => {
                setImmediate(() => {

                    const adaptCESNumber = (c: FAM_ICESNumber): ICESNumberEntity => ({
                        ...c,
                        name: c.cesNumber,
                        fields: [],
                    });
                    // TODO refactor to reduce memory footprint, has no dependency for hoisting

                    const mergedEntities = this.processEntity(
                        (this.originState.json.groupStructure?.cesNumbers ?? []).map(adaptCESNumber),
                        (this.targetState.json.groupStructure?.cesNumbers ?? []).map(adaptCESNumber),
                        [{ name: EModelContext.cesNumbers }],
                        this.getCESNumberProperties,
                        null,
                    );
                    resolve(mergedEntities);
                });
            });

        // Departments
        // -------------------------------
        // run in the next iteration of the event loop, a chance to run context tasks
        if (this.instructions.compareDepartments)
            this.departments = await new Promise((resolve) => {
                setImmediate(() => {

                    // making department compatible with IDataEntityLike, so we can use the same processEntity function
                    const adaptDepartment = (d: FAM_IDepartment): IDepartmentEntity => ({ ...d, name: d.name ?? "", });

                    const mergedEntities = this.processEntity(
                        (this.originState.json.groupStructure?.departments ?? []).map(adaptDepartment),
                        (this.targetState.json.groupStructure?.departments ?? []).map(adaptDepartment),
                        [{ name: EModelContext.departments }],
                        this.getDepartmentProperties,
                        null,
                    );
                    resolve(mergedEntities);
                });
            });

        // Non Standard Templates
        // -------------------------------
        // run in the next iteration of the event loop, a chance to run context tasks
        if (this.instructions.compareNonStandardTemplates)
            this.nonStandardTemplates = await new Promise((resolve) => {
                setImmediate(() => {
                    const mergedEntities = this.processEntity(
                        this.originState.json.nonStandardTemplates ?? [],
                        this.targetState.json.nonStandardTemplates ?? [],
                        [{ name: EModelContext.nonStandardTemplates }],
                        this.getNonStandardTemplateProperties,
                        null,
                    );
                    resolve(mergedEntities);
                });
            });

        // Fees and Premiums
        // -------------------------------
        // run in the next iteration of the event loop, a chance to run context tasks
        if (this.instructions.compareFees)
            this.feesAndPremiums = await new Promise((resolve) => {
                setImmediate(() => {
                    const mergedEntities = this.processEntity(
                        this.originState.json.feesAndPremiums,
                        this.targetState.json.feesAndPremiums,
                        [{ name: EModelContext.feesAndPremiums }],
                        null,
                        null,
                    );

                    resolve(mergedEntities);
                });
            });

        // Accounts Receivables
        // -------------------------------
        // run in the next iteration of the event loop, a chance to run context tasks
        if (this.instructions.compareClaimsARs)
            this.accountsReceivables = await new Promise((resolve) => {
                setImmediate(() => {
                    const mergedEntities = this.processEntity(
                        this.originState.json.claimsARs ?? [],
                        this.targetState.json.claimsARs ?? [],
                        [{ name: EModelContext.accountsReceivable }],
                        null,
                        null,

                    );

                    resolve(mergedEntities);
                });
            });

        // Stop Loss
        // -------------------------------
        // run in the next iteration of the event loop, a chance to run context tasks
        if (this.instructions.compareStopLoss)
            this.stopLosses = await new Promise((resolve) => {
                setImmediate(() => {
                    const mergedEntities = this.processEntity(
                        this.originState.json.stopLoss,
                        this.targetState.json.stopLoss,
                        [{ name: EModelContext.stopLoss }],
                        null,
                        null,
                    );

                    resolve(mergedEntities);
                });
            });

        // Fee Summary
        // -------------------------------
        // run in the next iteration of the event loop, a chance to run context tasks
        if (this.instructions.compareFees) {

            type returnResult = {
                feesSummary: Record<string, IComparable<IDataEntityLike, IDataEntityLikeMerged>>,
                stopLossSummary: Record<string, IComparable<IDataEntityLike, IDataEntityLikeMerged>>
            };

            const { feesSummary, stopLossSummary } = await new Promise<returnResult>((resolve) => {
                setImmediate(() => {
                    const summaries = this.processFeeSummaryAndStopLossSummary(
                        this.originState,
                        this.targetState,
                        [{ name: EModelContext.feesSummary }]
                    );

                    resolve({
                        feesSummary: summaries.feesSummary,
                        stopLossSummary: summaries.stopLossSummary
                    });

                });
            });

            this.feesSummary = feesSummary;
            this.stopLossSummary = stopLossSummary;
        }


        return {
            Root: this.groupConfiguration,
            Plans: this.plans,
            Benefits: this.benefits,
            GroupNumbers: this.groupNumbers,
            CESNumbers: this.cesNumbers,
            Departments: this.departments,
            NonStandardTemplates: this.nonStandardTemplates,
            StopLoss: this.stopLosses,
            feesAndPremiums: this.feesAndPremiums,
            feesSummary: this.feesSummary,
            stopLossSummary: this.stopLossSummary,
            claimsARs: this.accountsReceivables,
        };
    }

    private processFeeSummaryAndStopLossSummary(
        originState: IGCDataLight,
        targetState: IGCDataLight,
        context: ICompareContext[],
    ): {
        feesSummary: Record<string, IComparable<IDataEntityLike, IDataEntityLikeMerged>>,
        stopLossSummary: Record<string, IComparable<IDataEntityLike, IDataEntityLikeMerged>>,
    } {

        interface IFeeSummaryEntity extends IDataEntityLike {
            stopLossTypeName?: string;
            category: string;
            feesAndPremiumName: string;
            "Employee Only Rate": string;
            "Family Rate": string;
            "Employee + Child(ren) Rate": string;
            "Employee + Spouse Rate": string;
            "Employee + 1 (Child) Rate": string;
            "Two-Person Rate": string;
            "Employee + 2 Dependents Rate": string;
            "Concession Amount": string;
        }

        interface IAggregateNode {
            rowsAggregate?: Record<string, any>;
            grouping?: Record<string, IAggregateNode>;
        }

        const currencyFormatter = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });

        const toCurrency = (value: unknown): string => {
            if (value === null || value === undefined || value === '') {
                return currencyFormatter.format(0);
            }

            if (typeof value === 'number') {
                return Number.isFinite(value)
                    ? currencyFormatter.format(value)
                    : currencyFormatter.format(0);
            }

            if (typeof value === 'string') {
                const trimmed = value.trim();
                if (!trimmed) {
                    return currencyFormatter.format(0);
                }

                const isNegativeWrapped = trimmed.startsWith('(') && trimmed.endsWith(')');
                const normalized = trimmed
                    .replace(/[$,\s()]/g, '');
                const numeric = Number.parseFloat(normalized);

                if (Number.isFinite(numeric)) {
                    return currencyFormatter.format(isNegativeWrapped ? -numeric : numeric);
                }
            }

            return String(value);
        };

        const flattenFeeSummary = (agg: IAggregateNode): IFeeSummaryEntity[] => {

            const rows: IFeeSummaryEntity[] = [];
            if (agg.grouping) {
                Object.entries(agg.grouping).forEach(([categoryName, category]) => {
                    if (category.grouping) {
                        Object.entries(category.grouping).forEach(([feesAndPremiumName, feesAndPremium]) => {
                            if (feesAndPremium.rowsAggregate) {
                                const leaf = feesAndPremium.rowsAggregate;

                                rows.push({
                                    id: `${categoryName} - ${feesAndPremiumName}`,
                                    name: `${categoryName} - ${feesAndPremiumName}`,
                                    fields: [],
                                    deactivated: false,
                                    category: categoryName,
                                    feesAndPremiumName,
                                    "Employee Only Rate": toCurrency(leaf["Employee Only Rate"]),
                                    "Family Rate": toCurrency(leaf["Family Rate"]),
                                    "Employee + Child(ren) Rate": toCurrency(leaf["Employee + Child(ren) Rate"]),
                                    "Employee + Spouse Rate": toCurrency(leaf["Employee + Spouse Rate"]),
                                    "Employee + 1 (Child) Rate": toCurrency(leaf["Employee + 1 (Child) Rate"]),
                                    "Two-Person Rate": toCurrency(leaf["Two-Person Rate"]),
                                    "Employee + 2 Dependents Rate": toCurrency(leaf["Employee + 2 Dependents Rate"]),
                                    "Concession Amount": toCurrency(leaf["Concession Amount"]),
                                });
                            }

                        });
                    }
                });
            }

            return rows;
        };

        const flattenStopLossSummary = (agg: IAggregateNode): IFeeSummaryEntity[] => {

            const rows: IFeeSummaryEntity[] = [];
            if (agg.grouping) {
                Object.entries(agg.grouping).forEach(([stopLossTypeName, stopLossType]) => {
                    if (stopLossType.grouping) {
                        Object.entries(stopLossType.grouping).forEach(([categoryName, category]) => {
                            if (category.grouping) {
                                Object.entries(category.grouping).forEach(([feesAndPremiumName, feesAndPremium]) => {
                                    if (feesAndPremium.rowsAggregate) {
                                        const leaf = feesAndPremium.rowsAggregate;

                                        rows.push({
                                            id: `${stopLossTypeName} - ${categoryName} - ${feesAndPremiumName}`,
                                            name: `${stopLossTypeName} - ${categoryName} - ${feesAndPremiumName}`,
                                            fields: [],
                                            deactivated: false,
                                            stopLossTypeName,
                                            category: categoryName,
                                            feesAndPremiumName,
                                            "Employee Only Rate": toCurrency(leaf["Employee Only Rate"]),
                                            "Family Rate": toCurrency(leaf["Family Rate"]),
                                            "Employee + Child(ren) Rate": toCurrency(leaf["Employee + Child(ren) Rate"]),
                                            "Employee + Spouse Rate": toCurrency(leaf["Employee + Spouse Rate"]),
                                            "Employee + 1 (Child) Rate": toCurrency(leaf["Employee + 1 (Child) Rate"]),
                                            "Two-Person Rate": toCurrency(leaf["Two-Person Rate"]),
                                            "Employee + 2 Dependents Rate": toCurrency(leaf["Employee + 2 Dependents Rate"]),
                                            "Concession Amount": toCurrency(leaf["Concession Amount"]),
                                        });
                                    }
                                });
                            }
                        });
                    }
                });
            }

            return rows;
        };

        const originExtract = ExtractFees(originState.config, originState.json);
        const targetExtract = ExtractFees(targetState.config, targetState.json);

        const originFees = originExtract.feeSummary;
        const targetFees = targetExtract.feeSummary;

        const originStopLossRows = originExtract.stopLoss;
        const targetStopLossRows = targetExtract.stopLoss;

        const originFeeAggregates = flattenFeeSummary(
            groupByWithAggregate(
                originFees,
                ['category', 'feesAndPremiumName'],
                aggPredicate
            )
        );

        const targetFeeAggregates = flattenFeeSummary(
            groupByWithAggregate(
                targetFees,
                ['category', 'feesAndPremiumName'],
                aggPredicate
            )
        );

        const originStopLossAggregates = flattenStopLossSummary(
            groupByWithAggregate(
                originStopLossRows,
                ['stopLossTypeName', 'category', 'feesAndPremiumName'],
                aggPredicate
            )
        );

        const targetStopLossAggregates = flattenStopLossSummary(
            groupByWithAggregate(
                targetStopLossRows,
                ['stopLossTypeName', 'category', 'feesAndPremiumName'],
                aggPredicate
            )
        );

        const getFeeProperties = (entity: IFeeSummaryEntity): Record<string, string | undefined> => {
            return {
                category: entity.category,
                feesAndPremiumName: entity.feesAndPremiumName,
                "Employee Only Rate": entity["Employee Only Rate"],
                "Family Rate": entity["Family Rate"],
                "Employee + Child(ren) Rate": entity["Employee + Child(ren) Rate"],
                "Employee + Spouse Rate": entity["Employee + Spouse Rate"],
                "Employee + 1 (Child) Rate": entity["Employee + 1 (Child) Rate"],
                "Two-Person Rate": entity["Two-Person Rate"],
                "Employee + 2 Dependents Rate": entity["Employee + 2 Dependents Rate"],
                "Concession Amount": entity["Concession Amount"],
            };
        };


        const getStopLossProperties = (entity: IFeeSummaryEntity): Record<string, string | undefined> => {
            return {
                stopLossTypeName: entity.stopLossTypeName,
                category: entity.category,
                feesAndPremiumName: entity.feesAndPremiumName,
                "Employee Only Rate": entity["Employee Only Rate"],
                "Family Rate": entity["Family Rate"],
                "Employee + Child(ren) Rate": entity["Employee + Child(ren) Rate"],
                "Employee + Spouse Rate": entity["Employee + Spouse Rate"],
                "Employee + 1 (Child) Rate": entity["Employee + 1 (Child) Rate"],
                "Two-Person Rate": entity["Two-Person Rate"],
                "Employee + 2 Dependents Rate": entity["Employee + 2 Dependents Rate"],
                "Concession Amount": entity["Concession Amount"],
            };
        };

        const feesSummary = this.processEntity(
            originFeeAggregates,
            targetFeeAggregates,
            context,
            getFeeProperties,
            null,
        );

        const stopLossSummary = this.processEntity(
            originStopLossAggregates,
            targetStopLossAggregates,
            context,
            getStopLossProperties,
            (entity) => `${entity.stopLossTypeName ?? ""}|${entity.category ?? ""}|${entity.feesAndPremiumName ?? ""}`
        );

        return {
            feesSummary,
            stopLossSummary,
        };
    }


    /**
     * 
     * @param originEntities 
     * @param targetEntities 
     * @param getProperties 
     * @param getKeyPredicate predicate used to identify unique constraint
     * @returns 
     */
    private processEntity<TDataEntityLike extends IDataEntityLike>(
        originEntities: TDataEntityLike[],
        targetEntities: TDataEntityLike[],
        context: ICompareContext[],
        getProperties: ((entity: TDataEntityLike) => Record<string, string | undefined>) | null,
        getKeyPredicate: ((entity: TDataEntityLike) => string) | null,
        inheritedOriginTierMap?: Record<string, FAM_ITierLevel> | null,
        inheritedTargetTierMap?: Record<string, FAM_ITierLevel> | null,
    ): Record<string, IComparable<TDataEntityLike, IDataEntityLikeMerged>> {

        const defaultProperties = <T extends IDataEntityLike>(entity: T): Record<string, string | undefined> => {
            return {
                name: entity.name,
                deactivated: entity.deactivated ? "Yes" : "No",
            }
        }

        const defaultGetKeyPredicate = <T extends IDataEntityLike>(entity: T) => entity.id;

        const _getProperties = getProperties ?? defaultProperties;

        const _getKeyPredicate = getKeyPredicate ?? defaultGetKeyPredicate;

        const mergedEntities: Record<string, IComparable<TDataEntityLike, IDataEntityLikeMerged>> = {}

        // Merge Origins and Targets
        // --------------------------------------
        originEntities?.forEach((eachOrigin, index) => {

            const primaryKey = _getKeyPredicate(eachOrigin);

            let mergedEntity = mergedEntities[primaryKey];

            if (!mergedEntity)
                mergedEntity = mergedEntities[primaryKey] = {
                    primaryKey: primaryKey,
                    compareStatus: ECompareStatus.Unknown,
                    onOrigin: eachOrigin,
                    onTarget: undefined,
                    merged: undefined,
                    context: context,
                };

            mergedEntity.onOrigin = eachOrigin;
        });

        targetEntities?.forEach((eachTarget, index) => {

            const primaryKey = _getKeyPredicate(eachTarget);

            // first we try to find the benefit using id, then name
            let mergedEntity = mergedEntities[primaryKey];

            if (!mergedEntity)
                mergedEntity = mergedEntities[primaryKey] = {
                    primaryKey: primaryKey,
                    compareStatus: ECompareStatus.Unknown,
                    onOrigin: undefined,
                    onTarget: eachTarget,
                    merged: undefined,
                    context: context,
                };

            mergedEntity.onTarget = eachTarget;
        });

        // Merge Origins and Targets
        // --------------------------------------
        Object.entries(mergedEntities).forEach(([eachMergedEntityKey, eachMergedEntity]) => {

            const onOrigin = eachMergedEntity.onOrigin;
            const onTarget = eachMergedEntity.onTarget;

            // Status:
            // --------------------------------------
            // deleted, if we have an entity on the origin but not on the target
            if (onOrigin && !onTarget) {
                eachMergedEntity.compareStatus = ECompareStatus.OnOriginOnly;
            }

            // new, if we have an entity on the target but not on the origin
            if (!onOrigin && onTarget) {
                eachMergedEntity.compareStatus = ECompareStatus.OnTargetOnly;
            }

            // match, if we have an entity on both sides
            if (onOrigin && onTarget) {
                eachMergedEntity.compareStatus = ECompareStatus.Identical;
            }


            // OnMerge
            // --------------------------------------
            eachMergedEntity.merged = {
                key: eachMergedEntityKey,
                mergedProperties: {},
                mergedTiers: undefined,
                mergedReferences: undefined,
                mergedFields: undefined,
                mergedAttributeSets: undefined,
                mergedNonStandards: undefined,
            };

            // Properties 
            // -------------------------------
            eachMergedEntity.merged.mergedProperties = this.mergeProperties(
                onOrigin,
                onTarget,
                _getProperties
            );

            // Tiers
            // -------------------------------
            let hasTiers = false;

            if ((onOrigin?.tiers?.length ?? 0) > 0)
                hasTiers = true;

            if ((onTarget?.tiers?.length ?? 0) > 0)
                hasTiers = true;

            if (hasTiers) {
                eachMergedEntity.merged.mergedTiers = this.mergeTiers(
                    onOrigin?.tiers ?? [],
                    onTarget?.tiers ?? [],
                );
            }

            // Fields
            // -------------------------------
            const originTierMap = onOrigin?.tiers && ((onOrigin?.tiers?.length ?? 0) > 0)
                ? ArrayToLookupByKey(onOrigin?.tiers as FAM_ITierLevel[], 'id')
                : null;

            const targetTierMap = onTarget?.tiers && ((onTarget?.tiers?.length ?? 0) > 0)
                ? ArrayToLookupByKey(onTarget?.tiers as FAM_ITierLevel[], 'id')
                : null;

            // for non-standards to inherit the tier map from the parent entity (benefits)
            const effectiveOriginTierMap = originTierMap ?? inheritedOriginTierMap ?? null;
            const effectiveTargetTierMap = targetTierMap ?? inheritedTargetTierMap ?? null;

            eachMergedEntity.merged.mergedFields = this.mergeFields(
                onOrigin,
                onTarget,
                effectiveOriginTierMap,
                effectiveTargetTierMap,
            );

            // console.log(`processEntity: ${context.map(c => c.name).join(' > ')}: ${mergeKey}: mergedFields:`, mergedFields);

            // Attribute Sets
            // -------------------------------
            // any attribute sets? to process ?
            let hasAttributeSets = false;

            if ((onOrigin?.attributeSets?.length ?? 0) > 0)
                hasAttributeSets = true;

            if ((onTarget?.attributeSets?.length ?? 0) > 0)
                hasAttributeSets = true;

            if (hasAttributeSets) {

                // Merging AttributeSets
                const mergeAttributeSets = this.processEntity(
                    onOrigin?.attributeSets ?? [],
                    onTarget?.attributeSets ?? [],
                    [...context, { name: EModelContext.attributeSets }],
                    null,
                    null,
                    null,
                    null,
                );
                eachMergedEntity.merged.mergedAttributeSets = mergeAttributeSets;

                // Build a map of attribute sets by their reference id, so we can easily find them later
                {
                    const attributeSetsByRefId: typeof eachMergedEntity.merged.mergedAttributeSetsByRefId = {};

                    Object.entries(mergeAttributeSets).forEach(
                        ([key, eachMergedAttributeSet]) => {
                            const attributeSetRef = eachMergedAttributeSet.onTarget?.attributeSetRef ?? eachMergedAttributeSet.onOrigin?.attributeSetRef;

                            if (attributeSetRef) {
                                const list = attributeSetsByRefId[attributeSetRef.id] ?? [];
                                list.push(eachMergedAttributeSet);
                                attributeSetsByRefId[attributeSetRef.id] = list;
                            }
                        });

                    eachMergedEntity.merged.mergedAttributeSetsByRefId = attributeSetsByRefId;
                }
            }

            // Non Standards
            // -------------------------------
            // any non standards? to process ?

            let hasNonStandards = false;

            if ((onOrigin?.nonStandards?.length ?? 0) > 0)
                hasNonStandards = true;

            if ((onTarget?.nonStandards?.length ?? 0) > 0)
                hasNonStandards = true;

            if (hasNonStandards) {

                const mergedNonStandards = this.processEntity(
                    onOrigin?.nonStandards ?? [],
                    onTarget?.nonStandards ?? [],
                    [...context, { name: EModelContext.nonStandards }],
                    this.getNonStandardProperties,
                    null,
                    effectiveOriginTierMap,
                    effectiveTargetTierMap,
                );
                eachMergedEntity.merged.mergedNonStandards = mergedNonStandards;

                // Build a map of non standards by their section reference id, so we can easily find them later
                {
                    const nonStandardsBySectionRef: typeof eachMergedEntity.merged.mergedNonStandardsBySectionRef = {};

                    Object.entries(mergedNonStandards).forEach(([key, eachMergedNonstandard]) => {
                        const sectionRef = eachMergedNonstandard.onTarget?.sectionRef ?? eachMergedNonstandard.onOrigin?.sectionRef;

                        if (sectionRef) {
                            const list = nonStandardsBySectionRef[sectionRef.id] ?? [];
                            list.push(eachMergedNonstandard);
                            nonStandardsBySectionRef[sectionRef.id] = list;
                        }
                    });

                    eachMergedEntity.merged.mergedNonStandardsBySectionRef = nonStandardsBySectionRef;
                }
            }

        });

        return mergedEntities;
    }


    private processEntityPlan(
        mergedPlans: Record<string, IComparable<FAM_IPlan, IDataEntityLikeMerged>>
    ) {
        Object.entries(mergedPlans).forEach(([_, eachMergedPlan]) => {
            const onOriginPlan = eachMergedPlan.onOrigin;
            const onTargetPlan = eachMergedPlan.onTarget;

            const mergedReferences: Record<string, IComparable<string, string>> = {};

            onOriginPlan?.benefitRefs.forEach((eachBenefitRef, index) => {

                const primaryKey = eachBenefitRef.id!;

                let mergedRef = mergedReferences[primaryKey];

                if (!mergedRef) {
                    mergedRef = mergedReferences[primaryKey] = {
                        primaryKey: primaryKey,
                        compareStatus: ECompareStatus.Unknown,
                        onOrigin: eachBenefitRef.name,
                        onTarget: undefined,
                        merged: undefined,
                    };

                }
                mergedRef.onOrigin = eachBenefitRef.name;
            });

            onTargetPlan?.benefitRefs.forEach((eachBenefitRef, index) => {

                const primaryKey = eachBenefitRef.id!;

                let mergedRef = mergedReferences[primaryKey];

                if (!mergedRef) {
                    mergedRef = mergedReferences[primaryKey] = {
                        primaryKey: primaryKey,
                        compareStatus: ECompareStatus.Unknown,
                        onOrigin: undefined,
                        onTarget: eachBenefitRef.name,
                        merged: undefined,
                    };
                }

                mergedRef.onTarget = eachBenefitRef.name;
            });

            Object.entries(mergedReferences).forEach(([_, mergedReference]) => {

                const onOriginReference = mergedReference.onOrigin;
                const onTargetReference = mergedReference.onTarget;

                if (mergedReference.onOrigin === undefined && mergedReference.onTarget === undefined) {
                    // this should not happen, but in case it does, we consider it identical
                    mergedReference.compareStatus = ECompareStatus.Identical;
                } else if (mergedReference.onOrigin === undefined && mergedReference.onTarget !== undefined) {
                    // new reference added
                    mergedReference.compareStatus = ECompareStatus.OnTargetOnly;
                } else if (mergedReference.onOrigin !== undefined && mergedReference.onTarget === undefined) {
                    // reference removed
                    mergedReference.compareStatus = ECompareStatus.OnOriginOnly;
                } else if (onOriginReference === onTargetReference)
                    mergedReference.compareStatus = ECompareStatus.Identical;
                else {
                    mergedReference.compareStatus = ECompareStatus.Unknown;
                }
            });

            if (eachMergedPlan.merged) {
                eachMergedPlan.merged.mergedReferences = mergedReferences;
            }
        });
    }

    private processEntityBenefit<T extends IDataEntityLike>(
        originState: IGCDataLight,
        targetState: IGCDataLight,
        origins: T[],
        targets: T[],
        merged: Record<string, IComparable<FAM_IBenefit, IDataEntityLikeMerged>>,
    ) {

        // lets build a map of benefit id to the plans that reference them, so we can determine if a benefit is modified due to plan reference changes
        // benefitId -> [plan1, plan2] that reference this benefit id
        // TODO raise this as cache
        const benefitIdToPlanRefs: Record<string, { origin: FAM_IPlan[]; target: FAM_IPlan[] }> = {};

        originState.json.plans?.forEach((plan) => {
            plan.benefitRefs.forEach((benefitRef) => {
                const benefitId = benefitRef.id;
                const planRefs = benefitIdToPlanRefs[benefitId] ?? (benefitIdToPlanRefs[benefitId] = { origin: [], target: [] });
                planRefs.origin.push(plan);
            });
        });

        targetState.json.plans?.forEach((plan) => {
            plan.benefitRefs.forEach((benefitRef) => {
                const benefitId = benefitRef.id;
                const planRefs = benefitIdToPlanRefs[benefitId] ?? (benefitIdToPlanRefs[benefitId] = { origin: [], target: [] });
                planRefs.target.push(plan);
            });
        });

        Object.entries(merged).forEach(([mergedKey, eachMerged]) => {

            const mergedReferences: Record<string, IComparable<string, string>> = {};

            const plans = benefitIdToPlanRefs[mergedKey];

            if (!plans)
                return;

            plans.origin.forEach((eachPlan) => {
                const primaryKey = eachPlan.id!;

                let mergedRef = mergedReferences[primaryKey]

                if (!mergedRef) {
                    mergedRef = mergedReferences[primaryKey] = {
                        primaryKey: primaryKey,
                        compareStatus: ECompareStatus.Unknown,
                        onOrigin: eachPlan.name,
                        onTarget: undefined,
                        merged: undefined,
                    };
                }

                mergedRef.onOrigin = eachPlan.name;
            });

            plans.target.forEach((eachPlan) => {
                const primaryKey = eachPlan.id!;

                let mergedRef = mergedReferences[primaryKey]

                if (!mergedRef) {
                    mergedRef = mergedReferences[primaryKey] = {
                        primaryKey: primaryKey,
                        compareStatus: ECompareStatus.Unknown,
                        onOrigin: undefined,
                        onTarget: eachPlan.name,
                        merged: undefined,
                    };
                }

                mergedRef.onTarget = eachPlan.name;
            });

            Object.entries(mergedReferences).forEach(
                ([_, mergedReference]) => {
                    const onOriginReference = mergedReference.onOrigin;
                    const onTargetReference = mergedReference.onTarget;

                    if (onOriginReference === undefined && onTargetReference === undefined) {
                        mergedReference.compareStatus = ECompareStatus.Identical;
                    } else if (onOriginReference === undefined && onTargetReference !== undefined) {
                        mergedReference.compareStatus = ECompareStatus.OnTargetOnly;
                    } else if (onOriginReference !== undefined && onTargetReference === undefined) {
                        mergedReference.compareStatus = ECompareStatus.OnOriginOnly;
                    } else if (onOriginReference === onTargetReference) {
                        mergedReference.compareStatus = ECompareStatus.Identical;
                    } else {
                        mergedReference.compareStatus = ECompareStatus.Unknown;
                    }
                }
            );

            if (eachMerged.merged) {
                eachMerged.merged.mergedReferences = mergedReferences;
            }
        });
    }

    private mergeProperties = <TDataEntityLike extends IDataEntityLike>(
        onOriginEntity: TDataEntityLike | undefined,
        onTargetEntity: TDataEntityLike | undefined,
        getProperties: (entity: TDataEntityLike) => Record<string, string | undefined>,
    ) => {

        const onMergeProperties: Record<string, IComparable<string, string>> = {};

        const _process = (entity: TDataEntityLike, side: ESide) => {
            const entityProperties = getProperties(entity);

            Object.entries(entityProperties).forEach(([primaryKey, value]) => {

                let mergedProperty = onMergeProperties[primaryKey];

                if (!mergedProperty) {
                    // initialize properties if not already done
                    mergedProperty = onMergeProperties[primaryKey] = {
                        primaryKey: primaryKey,
                        compareStatus: ECompareStatus.Unknown,
                        onOrigin: undefined,
                        onTarget: undefined,
                        merged: undefined,
                    };
                }

                if (side === ESide.Origin) {
                    mergedProperty.onOrigin = value;
                } else {
                    mergedProperty.onTarget = value;
                }
            });
        }

        if (onTargetEntity)
            _process(onTargetEntity, ESide.Target);

        if (onOriginEntity)
            _process(onOriginEntity, ESide.Origin);

        // Reconcile properties
        Object.entries(onMergeProperties).forEach(([_, mergedProperty]) => {

            const onOriginFriendlyValue = mergedProperty.onOrigin;
            const onTargetFriendlyValue = mergedProperty.onTarget;

            if (onOriginFriendlyValue === undefined && onTargetFriendlyValue === undefined) {
                mergedProperty.compareStatus = ECompareStatus.Identical;
            } else if (onOriginFriendlyValue === onTargetFriendlyValue) {
                mergedProperty.compareStatus = ECompareStatus.Identical;
            } else if (onOriginFriendlyValue === undefined && onTargetFriendlyValue !== undefined) {
                // new property added
                mergedProperty.compareStatus = ECompareStatus.OnTargetOnly;
            } else if (onOriginFriendlyValue !== undefined && onTargetFriendlyValue === undefined) {
                // property removed
                mergedProperty.compareStatus = ECompareStatus.OnOriginOnly;
            }
        });

        return onMergeProperties;
    }

    private mergeFields<TDataEntityLike extends IDataEntityLike>(
        onOriginEntity: TDataEntityLike | undefined,
        onTargetEntity: TDataEntityLike | undefined,
        originTierMap: Record<string, FAM_ITierLevel> | null,
        targetTierMap: Record<string, FAM_ITierLevel> | null,
    ): Record<string, IComparable<FAM_IFlexibleField, IDataFlexibleFieldLikeMerged>> {

        // Fields
        // -------------------------------
        const onMergedFields: Record<string,
            IComparable<FAM_IFlexibleField, IDataFlexibleFieldLikeMerged>> = {};

        const _processFields = (
            onEntity: TDataEntityLike | undefined,
            side: ESide,
        ) => {

            if (!onEntity || !onEntity.fields || onEntity.fields.length === 0) {
                return;
            }

            onEntity.fields?.forEach((eachField, index) => {

                const config = side === ESide.Origin
                    ? this.originState.config
                    : this.targetState.config;

                const isHidden = (eachField.visibility ?? 0) > 0;

                const bcbs_config_attribute = config.cache.map_bcbs_config_attribute[eachField.configRef.id];

                const bcbs_config_masterattribute = bcbs_config_attribute
                    ? config.cache.map_bcbs_config_masterattribute[bcbs_config_attribute._bcbs_config_masterattributeid_value!]
                    : null;

                if (!bcbs_config_attribute) {
                    console.warn("bcbs_config_attribute not found", eachField.configRef.id);
                    return;
                }

                let skipAttribute = ShouldSkipAttribute(this.instructions.compareConfiguration ?? null, bcbs_config_attribute);

                if (skipAttribute) {
                    console.log("skipping attribute due to compare configuration", bcbs_config_attribute.bcbs_config_attributeid, bcbs_config_attribute.bcbs_name);
                    return;
                }

                if (isHidden) {
                    // skip hidden fields, we don't care about them
                    // console.verbose("skipping hidden field", bcbs_config_attribute.bcbs_config_attributeid, bcbs_config_attribute.bcbs_name);
                    return;
                }

                // we try to find the key using the master attribute id
                // and aggregate by the master attribute id keeping only the one that is visible
                let primaryKey = bcbs_config_attribute._bcbs_config_masterattributeid_value!;

                let mergedField = onMergedFields[primaryKey];

                if (!mergedField) {
                    // first encounter with this master attribute?
                    mergedField = onMergedFields[primaryKey] = {
                        primaryKey: primaryKey,
                        compareStatus: ECompareStatus.Unknown,
                        onOrigin: undefined,
                        onTarget: undefined,
                        merged: {
                            configRef: bcbs_config_attribute,
                            mergedValues: {},
                        },
                    };
                }

                if (side === ESide.Origin)
                    mergedField.onOrigin = eachField;
                else if (side === ESide.Target)
                    mergedField.onTarget = eachField;
                else
                    throw new Error("process: side must be Origin or Target");
            });
        }

        const _processFieldValues = (
            // onMerge: IFlexibleFieldValuesCompared,
            mergedField: IComparable<FAM_IFlexibleField, IDataFlexibleFieldLikeMerged>,
            tierMap: Record<string, FAM_ITierLevel> | null,
            onField: FAM_IFlexibleField | undefined,
            side: ESide,
        ) => {

            if (!onField || !onField.values || onField.values.length === 0) {
                return;
            }

            const config = side === ESide.Origin
                ? this.originState.config
                : this.targetState.config;

            const mergedValues = mergedField.merged?.mergedValues;

            if (!mergedValues) {
                throw new Error("mergedField.merged.values is undefined");
            }

            // Origin Values
            // -------------------------------
            onField.values.forEach((eachValue, index) => {

                if (!eachValue.value)
                    return;

                let primaryKey = eachValue.type;

                const tier = eachValue.tierRef && eachValue.tierRef.id && tierMap
                    ? tierMap[eachValue.tierRef.id]
                    : null;

                if (tier)
                    primaryKey += "," + tier.bcbs_name;

                let mergedValue = mergedValues[primaryKey];

                if (!mergedValue) {
                    mergedValue = {
                        primaryKey: primaryKey,
                        compareStatus: ECompareStatus.Unknown,
                        onOrigin: undefined,
                        onTarget: undefined,
                        merged: undefined,
                    };

                    // we don't need to keep the merged value, since we are already keeping the onOrigin and onTarget values
                    delete mergedValue.merged;

                    mergedValues[primaryKey] = mergedValue;
                }

                const bcbs_config_attribute = config.cache.map_bcbs_config_attribute[onField.configRef.id];

                const bcbs_config_masterattribute = bcbs_config_attribute
                    ? config.cache.map_bcbs_config_masterattribute[bcbs_config_attribute._bcbs_config_masterattributeid_value!]
                    : null;

                const fValue = eachValue.value && bcbs_config_masterattribute
                    ? friendlyValue(
                        bcbs_config_masterattribute.bcbs_attributetypecode as EControlType,
                        eachValue.value,
                        {
                            useDateFormatter: this.instructions.useDateFormatter,
                        }
                    )
                    : undefined;

                const sideValue: IFlexibleFieldValueMerged = {
                    order: index,
                    value: eachValue,
                    tier: tier,
                    friendlyValue: fValue,
                    field: onField,
                };

                if (side === ESide.Origin)
                    mergedValue.onOrigin = sideValue;
                else if (side === ESide.Target)
                    mergedValue.onTarget = sideValue;
                else
                    throw new Error("process: side must be Origin or Target");

            });

        }

        _processFields(onTargetEntity, ESide.Target);
        _processFields(onOriginEntity, ESide.Origin);

        // Fields - Values
        // -------------------------------
        Object.entries(onMergedFields).forEach(([key, mergedField]) => {

            mergedField.merged = {
                configRef: null as any,
                mergedValues: {},
            }

            _processFieldValues(
                mergedField,
                targetTierMap,
                mergedField.onTarget,
                ESide.Target);

            _processFieldValues(mergedField,
                originTierMap,
                mergedField.onOrigin,
                ESide.Origin);

            // Reconcile Values
            if (mergedField && mergedField.merged) {

                let isAllMatched = true;
                let hasTarget = false;
                let hasOrigin = false;

                Object.entries(mergedField.merged.mergedValues).forEach(([key, eachFieldValue]) => {

                    const onOrigin = eachFieldValue.onOrigin?.friendlyValue;
                    const onTarget = eachFieldValue.onTarget?.friendlyValue;

                    if (onOrigin === undefined && onTarget === undefined) {
                        eachFieldValue.compareStatus = ECompareStatus.Identical;
                    } else if (onOrigin === onTarget) {
                        eachFieldValue.compareStatus = ECompareStatus.Identical;
                    } else if (onOrigin === undefined && onTarget !== undefined) {
                        eachFieldValue.compareStatus = ECompareStatus.OnTargetOnly;
                        isAllMatched = false;
                        hasTarget = true;
                    } else if (onOrigin !== undefined && onTarget === undefined) {
                        eachFieldValue.compareStatus = ECompareStatus.OnOriginOnly;
                        isAllMatched = false;
                        hasOrigin = true;
                    } else {
                        eachFieldValue.compareStatus = ECompareStatus.Modified;
                        isAllMatched = false;
                        hasOrigin = true;
                        hasTarget = true;
                    }
                });

                if (isAllMatched) {
                    mergedField.compareStatus = ECompareStatus.Identical;
                } else {
                    if (hasOrigin && hasTarget) {
                        mergedField.compareStatus = ECompareStatus.Modified;
                    } else if (hasOrigin && !hasTarget) {
                        mergedField.compareStatus = ECompareStatus.OnOriginOnly;
                    } else if (!hasOrigin && hasTarget) {
                        mergedField.compareStatus = ECompareStatus.OnTargetOnly;
                    }
                }
            }
        });

        return onMergedFields;
    }

    private mergeTiers(
        onOriginTiers: IDataEntityTierLike[],
        onTargetTiers: IDataEntityTierLike[],
    ) {

        // Tiers
        // -------------------------------
        // TODO use the tier level id
        // TODO probably need to treat this as special entity, since:
        // - key matching is also special here, we need to compare level to level (tier to tier)
        // - we need to compare level to level (tier to tier) miss match for example single tier to two tiers
        // - we also need to compare properties of the tier (name, description, pcp-specialist spilt, in-network, out-of-network)
        // tierLevelIdMatcherFunction,
        //     tierPropertyMatcherFunction,

        const onMergeTiers: Record<string,
            IComparable<IDataEntityTierLike, IDataEntityTierLike>> = {};

        onOriginTiers?.forEach((eachTier, index) => {

            const primaryKey = eachTier.bcbs_name ?? eachTier.name;

            let mergedTier = onMergeTiers[primaryKey];

            if (!mergedTier)
                mergedTier = onMergeTiers[primaryKey] = {
                    primaryKey: primaryKey,
                    compareStatus: ECompareStatus.Unknown,
                    onOrigin: eachTier,
                    onTarget: undefined,
                    merged: undefined,
                };

            mergedTier.onOrigin = eachTier;
        });

        onTargetTiers?.forEach((eachTier, index) => {

            const primaryKey = eachTier.bcbs_name ?? eachTier.name;

            let mergedTier = onMergeTiers[primaryKey];

            if (!mergedTier)
                mergedTier = onMergeTiers[primaryKey] = {
                    primaryKey: primaryKey,
                    compareStatus: ECompareStatus.Unknown,
                    onOrigin: undefined,
                    onTarget: eachTier,
                    merged: undefined,
                };

            mergedTier.onTarget = eachTier;
        });


        Object.entries(onMergeTiers).forEach(([_, merged]) => {

            const onOriginFriendlyValue = merged.onOrigin?.bcbs_name;
            const onTargetFriendlyValue = merged.onTarget?.bcbs_name;

            if (onOriginFriendlyValue === undefined && onTargetFriendlyValue === undefined) {
                merged.compareStatus = ECompareStatus.Identical;
            } else if (onOriginFriendlyValue === onTargetFriendlyValue) {
                merged.compareStatus = ECompareStatus.Identical;
            } else if (onOriginFriendlyValue === undefined && onTargetFriendlyValue !== undefined) {
                merged.compareStatus = ECompareStatus.OnTargetOnly;
            } else if (onOriginFriendlyValue !== undefined && onTargetFriendlyValue === undefined) {
                merged.compareStatus = ECompareStatus.OnOriginOnly;
            }
        });

        return onMergeTiers;
    }

}

export const CompareProperties = (
    onOriginEntity: Record<string, string | null | undefined>,
    onTargetEntity: Record<string, string | null | undefined>,
    getCustomProperties?: (entity: Record<string, any>) => Record<string, string | undefined>
) => {

    const getDefaultProperties = (entity: Record<string, any>): Record<string, string | undefined> => {
        return entity;
    };

    const getProperties = getCustomProperties ?? getDefaultProperties;

    // Properties 
    // -------------------------------
    const onMergeProperties: Record<string, IComparable<string, undefined>> = {};

    if (onOriginEntity) {
        const entityProperties = getProperties(onOriginEntity);

        Object.entries(entityProperties).forEach(([key, value]) => {
            // initialize properties if not already done
            onMergeProperties[key] = onMergeProperties[key] ?? {};
            onMergeProperties[key].onOrigin = value;
        });
    }

    if (onTargetEntity) {
        const entityProperties = getProperties(onTargetEntity);

        Object.entries(entityProperties).forEach(([key, value]) => {
            // initialize properties if not already done
            onMergeProperties[key] = onMergeProperties[key] ?? {};
            onMergeProperties[key].onTarget = value;
        });
    }

    // Properties - Reconcile
    Object.entries(onMergeProperties).forEach(([_, mergedProperty]) => {

        const onOriginFriendlyValue = mergedProperty.onOrigin;
        const onTargetFriendlyValue = mergedProperty.onTarget;

        if (onOriginFriendlyValue === undefined && onTargetFriendlyValue === undefined) {
            mergedProperty.compareStatus = ECompareStatus.Identical;
        } else if (onOriginFriendlyValue === undefined && onTargetFriendlyValue !== undefined) {
            mergedProperty.compareStatus = ECompareStatus.OnTargetOnly;
        } else if (onOriginFriendlyValue !== undefined && onTargetFriendlyValue === undefined) {
            mergedProperty.compareStatus = ECompareStatus.OnOriginOnly;
        } else if (onOriginFriendlyValue === onTargetFriendlyValue)
            mergedProperty.compareStatus = ECompareStatus.Identical;
        else {
            mergedProperty.compareStatus = ECompareStatus.Unknown;
        }
    });

    return onMergeProperties
}


export const ShouldSkipAttribute = (
    compareConfiguration: ICompareConfiguration | null,
    bcbs_config_attribute: bcbs_config_attribute
) => {
    let skip = false;

    if (compareConfiguration && (
        compareConfiguration.underwritingReviewNecessary ||
        compareConfiguration.affectsIDCards ||
        compareConfiguration.targetedClaims
    )) {

        skip = true;

        if (compareConfiguration.underwritingReviewNecessary) {
            if (bcbs_config_attribute.bcbs_isunderwritingreviewnecessary === true) {
                skip = false;
            }
        }

        if (compareConfiguration.affectsIDCards) {
            if (bcbs_config_attribute.bcbs_isidcard === true) {
                skip = false;
            }
        }

        if (compareConfiguration.targetedClaims) {
            if (bcbs_config_attribute.bcbs_istargetedclaim) {
                skip = false;
            }
        }
    }
    return skip;
}
